import { GTM_ID, PROD } from '@config/env'
import { useUserContext } from '@contexts/user'
import { getLocalStorage, setLocalStorage } from '@helpers/storage'
import { LANDINGS } from '@routes/path'
import { useEffect, useRef } from 'react'
import TagManager from 'react-gtm-module'
import { useLocation } from 'react-router-dom'

export const useGTM = () => {
  const hasInitializedGTM = useRef(false)
  const hasPushedAuthData = useRef(false)

  const location = useLocation()

  const {
    isAuth,
    language,
    accountID,
    user,
    isLoading,
    isSupportMode,
    isSubaccount,
    planRecordKey,
    isRegistrationCompleted
  } = useUserContext()

  const getInitialDataLayer = () => {
    if (isAuth && language && user?.platform_stage) {
      return {
        login: 'true',
        lang: language,
        account_uuid: accountID,
        platform_stage: user.platform_stage,
        record_key: planRecordKey
      }
    }

    return {
      login: 'false'
    }
  }

  useEffect(() => {
    if (
      !PROD ||
      isLoading ||
      isSupportMode ||
      isSubaccount ||
      hasInitializedGTM.current
    ) {
      return
    }

    // Push data directly to window.dataLayer BEFORE GTM init.
    // TagManager.dataLayer() won't work here – it relies on GTM being already initialized.
    // We need this data to be available when GTM loads, like if it was in <head>.
    const initialData = getInitialDataLayer()
    window.dataLayer.push(initialData)

    TagManager.initialize({
      gtmId: GTM_ID
    })

    if (initialData.login === 'true') {
      hasPushedAuthData.current = true
    }

    hasInitializedGTM.current = true
  }, [isAuth, isLoading, isSupportMode, isSubaccount])

  useEffect(() => {
    if (
      !PROD ||
      isLoading ||
      isSupportMode ||
      isSubaccount ||
      !isAuth ||
      hasPushedAuthData.current ||
      !user?.platform_stage ||
      !language
    ) {
      return
    }

    TagManager.dataLayer({
      dataLayer: {
        login: 'true',
        lang: language,
        account_uuid: accountID,
        platform_stage: user.platform_stage,
        record_key: planRecordKey
      }
    })

    hasPushedAuthData.current = true
  }, [
    isAuth,
    language,
    accountID,
    user?.platform_stage,
    isLoading,
    isSupportMode,
    isSubaccount,
    planRecordKey
  ])

  const REGISTRATION_COMPLETED_EVENT = 'registrationCompletedEvent'

  useEffect(() => {
    const isWelcomePage = location.pathname === LANDINGS.WELCOME

    const wasSent = getLocalStorage(REGISTRATION_COMPLETED_EVENT)

    if (PROD && isWelcomePage && isRegistrationCompleted && !wasSent) {
      TagManager.dataLayer({
        dataLayer: {
          event: 'registration_completed',
          lang: language || 'en'
        }
      })

      setLocalStorage(REGISTRATION_COMPLETED_EVENT, 'true')
    }
  }, [location.pathname, isRegistrationCompleted, language])
}
